'use client';

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  History,
  Maximize2,
  Minimize2,
  Palette,
  Plus,
  RefreshCw,
  RotateCcw,
  Share2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import {
  SidePanelResizeHandle,
  useSidePanelSize,
} from '@/components/core/app-layout/SidePanelSize';
import { useDownloads } from '@/components/downloads/DownloadsProvider';
import { useAuth } from '@/app/auth-provider';
import { PreviewInspectorButton } from '@/components/preview/PreviewInspectorButton';
import { usePreviewAnnotation } from '@/components/preview/usePreviewAnnotation';
import { useAgentPreviewCapture } from '@/components/preview/useAgentPreviewCapture';
import { PreviewAnnotationToolbar } from '@/components/preview/PreviewAnnotationToolbar';
import { PreviewAnnotationOverlay } from '@/components/preview/PreviewAnnotationOverlay';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { useCanvasAwareness, useLivingDocsPanelClose } from '@/contexts/LivingDocsPanelContext';
import {
  CANVAS_LIVE_EDIT_STARTED_EVENT,
  RESPONSIVE_CANVAS_DISPLAY_FORMAT,
  type CanvasArtifactDetail,
  type CanvasArtifactSummary,
  type CanvasDisplayFormat,
  type CanvasElementSelection,
  type CanvasToolMetadata,
} from '@/lib/canvas/shared';
import { resolveCanvasSelectedVersion } from '@/lib/canvas/version-selection';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import type {
  CanvasPreviewMessageFromFrame,
  CanvasPreviewMessageToFrame,
} from './canvas-preview-bridge';
import { CanvasPreviewFrame } from './CanvasPreviewFrame';

const FIRST_PREVIEW_TIMEOUT_MS = 30_000;
const LIVE_PREVIEW_RECONNECT_GRACE_MS = 5_000;
const FIXED_CANVAS_GUTTER = 32;

type CanvasPreviewScaleMode = 'fit' | 'actual';

interface WorkspaceCanvasPanelProps {
  projectId: string;
  activeCanvas: CanvasToolMetadata | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBrowseLibrary?: () => void;
  onCanvasCreated?: () => void;
  onSelectCanvas: (canvas: CanvasToolMetadata) => void;
  onClearCanvas: (expectedCanvasId?: string) => void;
  onSelectionChange: (selection: CanvasElementSelection | null, version?: number | null) => void;
  onSelectionClearHandlerChange: (handler: (() => void) | null) => void;
}

function toMetadata(canvas: CanvasArtifactSummary): CanvasToolMetadata {
  return {
    id: canvas.id,
    title: canvas.title,
    status: canvas.status,
    version: canvas.currentVersion,
    lastSuccessfulVersion: canvas.lastSuccessfulVersion,
  };
}

function preserveCandidateVersion(
  metadata: CanvasToolMetadata,
  candidateId: string | null,
  candidateVersion: number
): CanvasToolMetadata {
  return metadata.id === candidateId &&
    (metadata.status === 'generating' || metadata.status === 'building') &&
    candidateVersion > metadata.version
    ? { ...metadata, version: candidateVersion }
    : metadata;
}

export function WorkspaceCanvasPanel({
  projectId,
  activeCanvas,
  open,
  onOpenChange,
  onBrowseLibrary,
  onCanvasCreated,
  onSelectCanvas,
  onClearCanvas,
  onSelectionChange,
  onSelectionClearHandlerChange,
}: WorkspaceCanvasPanelProps) {
  const { user } = useAuth();
  const { run: runDownload } = useDownloads();
  const { context: canvasContext } = useCanvasAwareness();
  const initialCanvasVersion =
    canvasContext?.artifactId === activeCanvas?.id ? canvasContext?.version || null : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const previewViewportRef = useRef<HTMLDivElement>(null);
  const previewSenderRef = useRef<((message: CanvasPreviewMessageToFrame) => void) | null>(null);
  const compiledPreviewSenderRef = useRef<((message: CanvasPreviewMessageToFrame) => void) | null>(
    null
  );
  const livePreviewSenderRef = useRef<((message: CanvasPreviewMessageToFrame) => void) | null>(
    null
  );
  const livePreviewReadyRef = useRef(false);
  const compiledPreviewReadyRef = useRef(false);
  const livePreviewUnavailableRef = useRef(false);
  const livePreviewDisconnectTimerRef = useRef<number | null>(null);
  const [canvases, setCanvases] = useState<CanvasArtifactSummary[]>([]);
  const [detail, setDetail] = useState<CanvasArtifactDetail | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewExpiresAt, setPreviewExpiresAt] = useState<string | null>(null);
  const [livePreviewUrl, setLivePreviewUrl] = useState<string | null>(null);
  const [livePreviewExpiresAt, setLivePreviewExpiresAt] = useState<string | null>(null);
  const [livePreviewVersion, setLivePreviewVersion] = useState<number | null>(null);
  const livePreviewSandboxIdRef = useRef<string | null>(null);
  const [livePreviewReady, setLivePreviewReady] = useState(false);
  const [previewFormat, setPreviewFormat] = useState<CanvasDisplayFormat>(
    RESPONSIVE_CANVAS_DISPLAY_FORMAT
  );
  const [livePreviewFormat, setLivePreviewFormat] = useState<CanvasDisplayFormat>(
    RESPONSIVE_CANVAS_DISPLAY_FORMAT
  );
  const [previewScaleMode, setPreviewScaleMode] = useState<CanvasPreviewScaleMode>('fit');
  const [previewViewportSize, setPreviewViewportSize] = useState({ width: 0, height: 0 });
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoringCanvasId, setRestoringCanvasId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(initialCanvasVersion);
  const [livePreviewEnabled, setLivePreviewEnabled] = useState(true);
  const [selectionMode, setSelectionMode] = useState(false);
  const [inspectorReady, setInspectorReady] = useState(false);
  const [previewTimedOut, setPreviewTimedOut] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  const [liveEditRequest, setLiveEditRequest] = useState(0);
  const { panelWidthValue: sharedPanelWidth } = useSidePanelSize();
  const lastSuccessfulVersionRef = useRef<number | null>(null);
  const activeCanvasIdRef = useRef(activeCanvas?.id || null);
  const activeCanvasVersionRef = useRef(activeCanvas?.version || 0);
  const projectIdRef = useRef(projectId);
  const selectedVersionRef = useRef(selectedVersion);
  const canvasListGenerationRef = useRef(0);
  const canvasDetailGenerationRef = useRef(0);
  const previewGenerationRef = useRef(0);
  const initializedCanvasIdRef = useRef<string | null>(null);
  const livePreviewGenerationRef = useRef(0);
  const livePreviewRequestRef = useRef<Promise<void> | null>(null);
  const livePreviewEnabledRef = useRef(true);
  activeCanvasIdRef.current = activeCanvas?.id || null;
  activeCanvasVersionRef.current = activeCanvas?.version || 0;
  projectIdRef.current = projectId;
  selectedVersionRef.current = selectedVersion;
  const displayLivePreview = Boolean(
    livePreviewEnabled && livePreviewUrl && (livePreviewReady || !previewUrl)
  );
  const displayedVersion = displayLivePreview
    ? livePreviewVersion
    : selectedVersion ||
      detail?.lastSuccessfulVersion ||
      activeCanvas?.lastSuccessfulVersion ||
      null;
  const exportReady = Boolean(
    previewUrl &&
      (!displayLivePreview || (displayedVersion && displayedVersion === selectedVersion))
  );
  const displayFormat = displayLivePreview ? livePreviewFormat : previewFormat;
  const fixedFormat = displayFormat.mode === 'fixed' ? displayFormat : null;
  const fixedScale = fixedFormat
    ? previewScaleMode === 'actual'
      ? 1
      : Math.max(
          0.05,
          Math.min(
            (previewViewportSize.width - FIXED_CANVAS_GUTTER) / fixedFormat.width,
            (previewViewportSize.height - FIXED_CANVAS_GUTTER) / fixedFormat.height
          )
        )
    : 1;
  const fixedDisplayWidth = fixedFormat ? fixedFormat.width * fixedScale : 0;
  const fixedDisplayHeight = fixedFormat ? fixedFormat.height * fixedScale : 0;
  const fixedStageWidth = Math.max(
    previewViewportSize.width,
    fixedDisplayWidth + FIXED_CANVAS_GUTTER
  );
  const fixedStageHeight = Math.max(
    previewViewportSize.height,
    fixedDisplayHeight + FIXED_CANVAS_GUTTER
  );
  const canvasFrameStyle: CSSProperties = {
    position: 'absolute',
    left: fixedFormat
      ? `${Math.max(FIXED_CANVAS_GUTTER / 2, (fixedStageWidth - fixedDisplayWidth) / 2)}px`
      : '0px',
    top: fixedFormat
      ? `${Math.max(FIXED_CANVAS_GUTTER / 2, (fixedStageHeight - fixedDisplayHeight) / 2)}px`
      : '0px',
    width: fixedFormat ? `${fixedFormat.width}px` : '100%',
    height: fixedFormat ? `${fixedFormat.height}px` : '100%',
    border: 0,
    background: 'white',
    transform: fixedFormat ? `scale(${fixedScale})` : 'none',
    transformOrigin: 'top left',
  };

  const clearSelectionVisual = useCallback(() => {
    previewSenderRef.current?.({ source: 'attune-host', type: 'clear-selection' });
  }, []);

  const annotation = usePreviewAnnotation({
    target:
      activeCanvas && displayedVersion
        ? { kind: 'canvas', artifactId: activeCanvas.id, version: displayedVersion }
        : null,
    enabled: open,
    resetKey: `${projectId}:${displayLivePreview ? livePreviewUrl : previewUrl}:${previewRetry}:${liveEditRequest}`,
    sendCapture: message => {
      if (!previewSenderRef.current) throw new Error('Preview is reconnecting. Try again.');
      previewSenderRef.current(message);
    },
    cancelCapture: requestId => {
      // Both frames can remain mounted while readiness switches the active sender.
      compiledPreviewSenderRef.current?.({
        source: 'attune-host',
        type: 'annotation-cancel',
        requestId,
      });
      livePreviewSenderRef.current?.({
        source: 'attune-host',
        type: 'annotation-cancel',
        requestId,
      });
    },
    beforeCapture: () => {
      setSelectionMode(false);
      previewSenderRef.current?.({ source: 'attune-host', type: 'selection-mode', enabled: false });
      clearSelectionVisual();
      onSelectionChange(null, displayedVersion);
    },
  });
  const { receive: receiveAnnotation, reset: resetAnnotation } = annotation;

  const handlePreviewMessage = useCallback(
    (data: CanvasPreviewMessageFromFrame) => {
      if (data.source === 'attune-preview-annotation') {
        receiveAnnotation(data);
        return;
      }
      // Readiness comes from the frame lifecycle, including inspector-state
      // acknowledgements when a template's initial ready message was missed.
      if (data.type === 'ready') return;
      if (data.type === 'inspector-state') {
        setSelectionMode(Boolean(data.enabled));
        return;
      }
      if (data.type === 'preview-updating') {
        resetAnnotation();
        return;
      }
      if (!data.selection.nodeId || !data.selection.bounds) return;
      onSelectionChange(data.selection, displayedVersion);
    },
    [displayedVersion, onSelectionChange, receiveAnnotation, resetAnnotation]
  );

  const handlePreviewSenderChange = useCallback(
    (sender: ((message: CanvasPreviewMessageToFrame) => void) | null) => {
      previewSenderRef.current = sender;
    },
    []
  );

  const handleCompiledPreviewSenderChange = useCallback(
    (sender: ((message: CanvasPreviewMessageToFrame) => void) | null) => {
      compiledPreviewSenderRef.current = sender;
      if (!sender) compiledPreviewReadyRef.current = false;
      if (!livePreviewReadyRef.current) handlePreviewSenderChange(sender);
    },
    [handlePreviewSenderChange]
  );

  const handleLivePreviewSenderChange = useCallback(
    (sender: ((message: CanvasPreviewMessageToFrame) => void) | null) => {
      livePreviewSenderRef.current = sender;
      if (livePreviewReadyRef.current) handlePreviewSenderChange(sender);
    },
    [handlePreviewSenderChange]
  );

  const handleCompiledPreviewReadyChange = useCallback((ready: boolean) => {
    compiledPreviewReadyRef.current = ready;
    if (!livePreviewReadyRef.current) setInspectorReady(ready);
  }, []);

  const handleLivePreviewReadyChange = useCallback(
    (ready: boolean) => {
      if (livePreviewDisconnectTimerRef.current !== null) {
        window.clearTimeout(livePreviewDisconnectTimerRef.current);
        livePreviewDisconnectTimerRef.current = null;
      }
      // Keep healthy HMR mounted, but do not let revoked access cover the
      // compiled preview indefinitely after the runtime hibernates.
      if (!ready && livePreviewReadyRef.current) {
        setInspectorReady(false);
        livePreviewDisconnectTimerRef.current = window.setTimeout(() => {
          livePreviewDisconnectTimerRef.current = null;
          livePreviewUnavailableRef.current = true;
          livePreviewReadyRef.current = false;
          setLivePreviewReady(false);
          setInspectorReady(compiledPreviewReadyRef.current);
          handlePreviewSenderChange(compiledPreviewSenderRef.current);
        }, LIVE_PREVIEW_RECONNECT_GRACE_MS);
        return;
      }
      if (ready) livePreviewUnavailableRef.current = false;
      livePreviewReadyRef.current = ready;
      setLivePreviewReady(ready);
      setInspectorReady(ready || compiledPreviewReadyRef.current);
      handlePreviewSenderChange(
        ready ? livePreviewSenderRef.current : compiledPreviewSenderRef.current
      );
    },
    [handlePreviewSenderChange]
  );

  const handleCompiledPreviewMessage = useCallback(
    (message: CanvasPreviewMessageFromFrame) => {
      if (!livePreviewReadyRef.current) handlePreviewMessage(message);
    },
    [handlePreviewMessage]
  );

  const handleLivePreviewMessage = useCallback(
    (message: CanvasPreviewMessageFromFrame) => {
      if (livePreviewReadyRef.current) handlePreviewMessage(message);
    },
    [handlePreviewMessage]
  );

  useEffect(() => {
    onSelectionClearHandlerChange(clearSelectionVisual);
    return () => onSelectionClearHandlerChange(null);
  }, [clearSelectionVisual, onSelectionClearHandlerChange]);

  useEffect(
    () => () => {
      if (livePreviewDisconnectTimerRef.current !== null) {
        window.clearTimeout(livePreviewDisconnectTimerRef.current);
      }
    },
    []
  );

  const authorizedFetch = useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      if (!user) throw new Error('Sign in to use Canvas');
      const token = await user.getIdToken();
      return fetch(input, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${token}` },
      });
    },
    [user]
  );

  useAgentPreviewCapture({
    enabled: open && Boolean(user),
    matches: request => request.target.kind === 'canvas',
    capture: async (request, signal) => {
      if (request.target.kind !== 'canvas')
        return { status: 'unavailable', error: 'Canvas target required.' };
      const response = await authorizedFetch(
        `/api/canvas/${encodeURIComponent(request.target.artifactId)}/preview?version=${request.target.version}`,
        { signal }
      );
      const preview = (await response.json()) as {
        url?: string;
        version?: number;
        format?: CanvasDisplayFormat;
        error?: string;
      };
      if (response.status === 202)
        return { status: 'pending', error: 'The requested Canvas version is not ready.' };
      if (!response.ok || !preview.url)
        return { status: 'unavailable', error: preview.error || 'Canvas preview is unavailable.' };
      if (preview.version !== request.target.version)
        return { status: 'stale', error: 'The Canvas preview version changed.' };
      const { captureCanvasFrame } = await import('@/components/preview/capture-frames');
      const format = preview.format;
      return captureCanvasFrame({
        request,
        signal,
        url: preview.url,
        width: format?.mode === 'fixed' ? format.width : previewViewportSize.width,
        height: format?.mode === 'fixed' ? format.height : previewViewportSize.height,
      });
    },
  });

  const loadCanvases = useCallback(
    async (interactive = false) => {
      if (!user || !projectId) return;
      const generation = ++canvasListGenerationRef.current;
      const targetProjectId = projectId;
      if (interactive) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await authorizedFetch(
          `/api/canvas?projectId=${encodeURIComponent(projectId)}&includeArchived=true`
        );
        const payload = (await response.json().catch(() => ({}))) as {
          canvases?: CanvasArtifactSummary[];
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error || 'Failed to load canvases');
        if (
          generation !== canvasListGenerationRef.current ||
          projectIdRef.current !== targetProjectId
        ) {
          return;
        }
        const next = payload.canvases || [];
        setCanvases(next);
        const currentCanvasId = activeCanvasIdRef.current;
        const current = currentCanvasId ? next.find(item => item.id === currentCanvasId) : null;
        if (current?.status === 'archived') {
          onClearCanvas(current.id);
          return;
        }
        // The project list is intentionally bounded. Preserve an older active or
        // deep-linked Canvas and let its detail request decide whether it exists.
        if (currentCanvasId && !current) return;
        const active = current || next.find(item => item.status !== 'archived');
        if (active) {
          onSelectCanvas(
            preserveCandidateVersion(
              toMetadata(active),
              currentCanvasId,
              activeCanvasVersionRef.current
            )
          );
        }
      } catch (error) {
        if (interactive)
          toast.error(error instanceof Error ? error.message : 'Canvas refresh failed');
      } finally {
        if (
          generation === canvasListGenerationRef.current &&
          projectIdRef.current === targetProjectId
        ) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [authorizedFetch, onClearCanvas, onSelectCanvas, projectId, user]
  );

  const loadActiveCanvas = useCallback(async () => {
    if (!activeCanvas?.id || !user) return;
    const targetCanvasId = activeCanvas.id;
    const generation = ++canvasDetailGenerationRef.current;
    const response = await authorizedFetch(`/api/canvas/${encodeURIComponent(targetCanvasId)}`);
    const payload = (await response.json().catch(() => ({}))) as {
      canvas?: CanvasArtifactDetail;
      error?: string;
    };
    if (
      generation !== canvasDetailGenerationRef.current ||
      activeCanvasIdRef.current !== targetCanvasId
    ) {
      return;
    }
    if (response.status === 404) {
      onClearCanvas(targetCanvasId);
      return;
    }
    if (!response.ok || !payload.canvas) throw new Error(payload.error || 'Failed to load canvas');
    setDetail(payload.canvas);
    const metadata = toMetadata(payload.canvas);
    const buildingVersion = payload.canvas.versions.find(version => version.status === 'building');
    onSelectCanvas(
      preserveCandidateVersion(
        metadata,
        activeCanvasIdRef.current,
        Math.max(activeCanvasVersionRef.current, buildingVersion?.version || 0)
      )
    );
  }, [activeCanvas?.id, authorizedFetch, onClearCanvas, onSelectCanvas, user]);

  const loadLivePreview = useCallback(
    async (renewAccess = false) => {
      if (!activeCanvas?.id || !user || !livePreviewEnabledRef.current) return;
      const targetCanvasId = activeCanvas.id;
      while (livePreviewRequestRef.current) {
        // Serialize fresh snapshots: a completion refresh must not reuse an
        // earlier building response, and metadata cannot satisfy token renewal.
        await livePreviewRequestRef.current.catch(() => undefined);
        if (activeCanvasIdRef.current !== targetCanvasId || !livePreviewEnabledRef.current) return;
      }
      const generation = ++livePreviewGenerationRef.current;
      const request = (async () => {
        const response = await authorizedFetch(
          `/api/canvas/${encodeURIComponent(targetCanvasId)}/session`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'live',
              sandboxId: renewAccess ? undefined : livePreviewSandboxIdRef.current,
            }),
          }
        );
        const payload = (await response.json().catch(() => ({}))) as {
          url?: string;
          version?: number;
          sandboxId?: string;
          expiresAt?: string;
          format?: CanvasDisplayFormat;
          error?: string;
        };
        if (
          generation !== livePreviewGenerationRef.current ||
          activeCanvasIdRef.current !== targetCanvasId ||
          !livePreviewEnabledRef.current
        ) {
          return;
        }
        if (!response.ok && response.status !== 202) {
          throw new Error(payload.error || 'Live preview failed');
        }
        if (payload.url) {
          setLivePreviewUrl(payload.url);
          setLivePreviewExpiresAt(payload.expiresAt || null);
        }
        livePreviewSandboxIdRef.current = payload.sandboxId || null;
        setLivePreviewVersion(payload.version || null);
        setLivePreviewFormat(payload.format || RESPONSIVE_CANVAS_DISPLAY_FORMAT);
        if (response.status === 202) {
          setLivePreviewUrl(null);
          setLivePreviewExpiresAt(null);
        }
      })();
      livePreviewRequestRef.current = request;
      try {
        await request;
      } finally {
        if (livePreviewRequestRef.current === request) livePreviewRequestRef.current = null;
      }
    },
    [activeCanvas?.id, authorizedFetch, user]
  );

  const loadPreview = useCallback(async () => {
    if (!activeCanvas?.id || !user) return;
    const targetCanvasId = activeCanvas.id;
    const targetVersion = selectedVersion;
    const generation = ++previewGenerationRef.current;
    const query = targetVersion ? `?version=${targetVersion}` : '';
    const response = await authorizedFetch(
      `/api/canvas/${encodeURIComponent(targetCanvasId)}/preview${query}`
    );
    const payload = (await response.json().catch(() => ({}))) as {
      url?: string;
      version?: number;
      kind?: string;
      expiresAt?: string;
      format?: CanvasDisplayFormat;
      error?: string;
    };
    if (
      generation !== previewGenerationRef.current ||
      activeCanvasIdRef.current !== targetCanvasId ||
      selectedVersionRef.current !== targetVersion
    ) {
      return;
    }
    if (!response.ok && response.status !== 202) throw new Error(payload.error || 'Preview failed');
    setPreviewUrl(payload.url || null);
    setPreviewExpiresAt(payload.expiresAt || null);
    if (payload.url) {
      const format = payload.format || RESPONSIVE_CANVAS_DISPLAY_FORMAT;
      setPreviewFormat(format);
    }
    if (!targetVersion && payload.version) setSelectedVersion(payload.version);
  }, [activeCanvas?.id, authorizedFetch, selectedVersion, user]);

  const refreshCanvas = useCallback(async () => {
    try {
      await Promise.all([
        loadCanvases(true),
        ...(activeCanvas?.id
          ? [loadActiveCanvas(), displayLivePreview ? loadLivePreview() : loadPreview()]
          : []),
      ]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Canvas refresh failed');
    }
  }, [
    activeCanvas?.id,
    displayLivePreview,
    loadActiveCanvas,
    loadCanvases,
    loadLivePreview,
    loadPreview,
  ]);

  useEffect(() => {
    if (!open) return;
    void loadCanvases();
  }, [open, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const canvasId = activeCanvas?.id || null;
    if (initializedCanvasIdRef.current === canvasId) {
      if (open && canvasId)
        void loadActiveCanvas().catch(error =>
          toast.error(error instanceof Error ? error.message : 'Failed to load canvas')
        );
      return;
    }
    initializedCanvasIdRef.current = canvasId;
    canvasDetailGenerationRef.current += 1;
    previewGenerationRef.current += 1;
    livePreviewGenerationRef.current += 1;
    livePreviewRequestRef.current = null;
    livePreviewEnabledRef.current = true;
    setLivePreviewEnabled(true);
    if (livePreviewDisconnectTimerRef.current !== null) {
      window.clearTimeout(livePreviewDisconnectTimerRef.current);
      livePreviewDisconnectTimerRef.current = null;
    }
    livePreviewUnavailableRef.current = false;
    setDetail(null);
    setPreviewUrl(null);
    setPreviewExpiresAt(null);
    setLivePreviewUrl(null);
    setLivePreviewExpiresAt(null);
    setLivePreviewVersion(null);
    livePreviewSandboxIdRef.current = null;
    setLivePreviewReady(false);
    livePreviewReadyRef.current = false;
    setPreviewFormat(RESPONSIVE_CANVAS_DISPLAY_FORMAT);
    setLivePreviewFormat(RESPONSIVE_CANVAS_DISPLAY_FORMAT);
    setPreviewScaleMode('fit');
    setPreviewTimedOut(false);
    setPreviewRetry(0);
    setLiveEditRequest(0);
    setSelectedVersion(initialCanvasVersion);
    lastSuccessfulVersionRef.current = null;
    onSelectionChange(null, initialCanvasVersion || activeCanvas?.lastSuccessfulVersion);
    if (!open || !activeCanvas?.id) return;
    void loadActiveCanvas().catch(error =>
      toast.error(error instanceof Error ? error.message : 'Failed to load canvas')
    );
  }, [activeCanvas?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!livePreviewUrl) {
      if (livePreviewDisconnectTimerRef.current !== null) {
        window.clearTimeout(livePreviewDisconnectTimerRef.current);
        livePreviewDisconnectTimerRef.current = null;
      }
      livePreviewUnavailableRef.current = false;
      livePreviewReadyRef.current = false;
      setLivePreviewReady(false);
      setInspectorReady(compiledPreviewReadyRef.current);
      handlePreviewSenderChange(compiledPreviewSenderRef.current);
    }
  }, [handlePreviewSenderChange, livePreviewUrl]);

  useEffect(() => {
    if (!open || !activeCanvas?.id || !activeCanvas.lastSuccessfulVersion) return;
    const previousLastSuccessfulVersion = lastSuccessfulVersionRef.current;
    const nextLastSuccessfulVersion = activeCanvas.lastSuccessfulVersion;
    lastSuccessfulVersionRef.current = nextLastSuccessfulVersion;
    setSelectedVersion(current =>
      resolveCanvasSelectedVersion({
        selectedVersion: current,
        previousLastSuccessfulVersion,
        nextLastSuccessfulVersion,
      })
    );
  }, [activeCanvas?.id, activeCanvas?.lastSuccessfulVersion, open]);

  useEffect(() => {
    if (!open || !activeCanvas?.id) return;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadActiveCanvas().catch(() => undefined);
      }
    };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [activeCanvas?.id, loadActiveCanvas, open]);

  useEffect(() => {
    if (
      !open ||
      !activeCanvas?.id ||
      previewUrl ||
      livePreviewUrl ||
      activeCanvas.status === 'build_failed'
    ) {
      setPreviewTimedOut(false);
      return;
    }
    const timeout = window.setTimeout(() => setPreviewTimedOut(true), FIRST_PREVIEW_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [activeCanvas?.id, activeCanvas?.status, livePreviewUrl, open, previewRetry, previewUrl]);

  useEffect(() => {
    if (!open || !activeCanvas?.id) return;
    onSelectionChange(null, displayedVersion);
    clearSelectionVisual();
  }, [activeCanvas?.id, clearSelectionVisual, displayedVersion, onSelectionChange, open]);

  useEffect(() => {
    if (!open || !activeCanvas?.id) return;
    void loadPreview().catch(error =>
      toast.error(error instanceof Error ? error.message : 'Failed to load preview')
    );
  }, [activeCanvas?.id, loadPreview, open, selectedVersion]);

  useEffect(() => {
    const startLiveEdit = (event: Event) => {
      const canvasId = (event as CustomEvent<{ canvasId?: string }>).detail?.canvasId;
      if (open && canvasId && canvasId === activeCanvasIdRef.current) {
        if (livePreviewEnabledRef.current && livePreviewUnavailableRef.current) {
          livePreviewGenerationRef.current += 1;
          livePreviewRequestRef.current = null;
          livePreviewUnavailableRef.current = false;
          setLivePreviewUrl(null);
          setLivePreviewExpiresAt(null);
          livePreviewSandboxIdRef.current = null;
          setLivePreviewVersion(null);
        }
        setLiveEditRequest(current => current + 1);
      }
    };
    window.addEventListener(CANVAS_LIVE_EDIT_STARTED_EVENT, startLiveEdit);
    return () => window.removeEventListener(CANVAS_LIVE_EDIT_STARTED_EVENT, startLiveEdit);
  }, [open]);

  useEffect(() => {
    if (!open || !activeCanvas || !['generating', 'building'].includes(activeCanvas.status)) return;
    let cancelled = false;
    let timer: number | null = null;
    const poll = async () => {
      await loadActiveCanvas().catch(() => undefined);
      if (!cancelled) timer = window.setTimeout(() => void poll(), 1_500);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [activeCanvas?.id, activeCanvas?.status, loadActiveCanvas, open]);

  useEffect(() => {
    const waitingForBuild = Boolean(
      activeCanvas && ['generating', 'building'].includes(activeCanvas.status)
    );
    if (
      !open ||
      !activeCanvas?.id ||
      !livePreviewEnabled ||
      (!waitingForBuild && liveEditRequest === 0 && !livePreviewUrl)
    ) {
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    const startedAt = Date.now();
    const poll = async () => {
      await loadLivePreview().catch(() => undefined);
      if (
        !cancelled &&
        (waitingForBuild ||
          (liveEditRequest > 0 && Date.now() - startedAt < FIRST_PREVIEW_TIMEOUT_MS))
      ) {
        timer = window.setTimeout(() => void poll(), 1_000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [
    activeCanvas?.id,
    activeCanvas?.status,
    activeCanvas?.version,
    liveEditRequest,
    livePreviewEnabled,
    livePreviewUrl,
    loadLivePreview,
    open,
  ]);

  useEffect(() => {
    if (!open || !previewUrl || displayLivePreview || !previewExpiresAt) return;
    const expiresAt = Date.parse(previewExpiresAt);
    const delay = Number.isFinite(expiresAt)
      ? Math.max(1_000, expiresAt - Date.now() - 60_000)
      : 14 * 60_000;
    const timer = window.setTimeout(() => void loadPreview().catch(() => undefined), delay);
    return () => window.clearTimeout(timer);
  }, [displayLivePreview, loadPreview, open, previewExpiresAt, previewUrl]);

  useEffect(() => {
    if (!open || !livePreviewUrl || !livePreviewExpiresAt) return;
    const expiresAt = Date.parse(livePreviewExpiresAt);
    const delay = Number.isFinite(expiresAt)
      ? Math.max(1_000, expiresAt - Date.now() - 60_000)
      : 14 * 60_000;
    const timer = window.setTimeout(() => void loadLivePreview(true).catch(() => undefined), delay);
    return () => window.clearTimeout(timer);
  }, [livePreviewExpiresAt, livePreviewUrl, loadLivePreview, open]);

  const createBlank = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const response = await authorizedFetch('/api/canvas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          title: 'Untitled canvas',
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        canvas?: CanvasArtifactSummary;
        version?: number;
        error?: string;
      };
      if (!response.ok || !payload.canvas)
        throw new Error(payload.error || 'Failed to create canvas');
      setCanvases(current => [payload.canvas!, ...current]);
      onSelectCanvas({
        ...toMetadata(payload.canvas),
        version: payload.version || payload.canvas.currentVersion,
      });
      onCanvasCreated?.();
      toast.success(
        'Canvas is preparing. You can describe what to make when the first build is ready.'
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create canvas');
    } finally {
      setCreating(false);
    }
  }, [authorizedFetch, creating, onCanvasCreated, onSelectCanvas, projectId]);

  const restoreArchived = useCallback(
    async (canvasId: string) => {
      if (restoringCanvasId) return;
      setRestoringCanvasId(canvasId);
      try {
        const response = await authorizedFetch(`/api/canvas/${encodeURIComponent(canvasId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'restore', projectId }),
        });
        const payload = (await response.json().catch(() => ({}))) as {
          canvas?: CanvasArtifactSummary;
          error?: string;
        };
        if (!response.ok || !payload.canvas) {
          throw new Error(payload.error || 'Failed to restore canvas');
        }
        setCanvases(current =>
          current.map(canvas => (canvas.id === payload.canvas!.id ? payload.canvas! : canvas))
        );
        onSelectCanvas(toMetadata(payload.canvas));
        toast.success('Canvas restored.');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to restore canvas');
      } finally {
        setRestoringCanvasId(null);
      }
    },
    [authorizedFetch, onSelectCanvas, projectId, restoringCanvasId]
  );

  const download = useCallback(
    async (format: string) => {
      if (!activeCanvas?.id || !exportReady) return;
      try {
        await runDownload({ projectId, name: activeCanvas.title || 'Canvas', format }, () => {
          const query = selectedVersion ? `?version=${selectedVersion}` : '';
          return authorizedFetch(
            `/api/canvas/${encodeURIComponent(activeCanvas.id)}/export/${format}${query}`
          );
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Export failed');
      }
    },
    [
      activeCanvas?.id,
      activeCanvas?.title,
      authorizedFetch,
      exportReady,
      runDownload,
      projectId,
      selectedVersion,
    ]
  );

  const share = useCallback(async () => {
    if (!activeCanvas?.id) return;
    const url = new URL('/app/workspace', window.location.origin);
    url.searchParams.set('canvas', activeCanvas.id);
    await navigator.clipboard.writeText(url.toString());
    toast.success('Authenticated Canvas link copied.');
  }, [activeCanvas?.id]);

  const statusColor =
    activeCanvas?.status === 'ready'
      ? 'green'
      : activeCanvas?.status === 'build_failed'
        ? 'red'
        : 'orange';
  const readyVersions = useMemo(
    () => detail?.versions.filter(version => version.status === 'ready') || [],
    [detail?.versions]
  );

  const selectVersion = useCallback(
    (version: number) => {
      const enableLivePreview = version === activeCanvas?.lastSuccessfulVersion;
      livePreviewEnabledRef.current = enableLivePreview;
      setLivePreviewEnabled(enableLivePreview);
      if (!enableLivePreview) {
        if (livePreviewDisconnectTimerRef.current !== null) {
          window.clearTimeout(livePreviewDisconnectTimerRef.current);
          livePreviewDisconnectTimerRef.current = null;
        }
        livePreviewUnavailableRef.current = false;
        // Invalidate an already running request before it can replace history.
        livePreviewGenerationRef.current += 1;
        livePreviewRequestRef.current = null;
        setLivePreviewUrl(null);
        setLivePreviewExpiresAt(null);
        livePreviewSandboxIdRef.current = null;
        setLivePreviewVersion(null);
      }
      setSelectedVersion(version);
    },
    [activeCanvas?.lastSuccessfulVersion]
  );

  useEffect(() => {
    if (!open || !previewViewportRef.current) return;
    const viewport = previewViewportRef.current;
    const measure = () => {
      const bounds = viewport.getBoundingClientRect();
      setPreviewViewportSize({ width: bounds.width, height: bounds.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [activeCanvas?.id, livePreviewUrl, open, previewUrl]);

  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === panelRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement === panelRef.current) await document.exitFullscreen();
      else await panelRef.current?.requestFullscreen();
    } catch {
      toast.error('Fullscreen is unavailable in this browser.');
    }
  }, []);

  useLivingDocsPanelClose(() => onOpenChange(false), open);

  if (!open) return null;

  const panelWidthValue = fullscreen ? '100vw' : sharedPanelWidth;

  return (
    <Flex
      ref={panelRef}
      as="aside"
      aria-label="Canvas editor"
      data-section="workspaceCanvasPanel"
      position="relative"
      zIndex="1"
      w={panelWidthValue}
      minW={panelWidthValue}
      flexShrink={0}
      borderLeftWidth="1px"
      borderColor="ui.border.subtle"
      bg="ui.surface.rail"
      boxShadow="var(--attune-shadow-elevated, none)"
      direction="column"
      h={fullscreen ? '100vh' : 'full'}
      minH={0}
      overscrollBehavior="contain"
    >
      <SidePanelResizeHandle label="Resize Canvas editor" disabled={fullscreen} />
      <HStack
        minH="54px"
        px="3"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        bg="ui.surface.rail"
        justify="space-between"
        flexShrink={0}
      >
        <HStack gap="2" minW={0}>
          {onBrowseLibrary ? (
            <IconButton
              aria-label="Back to Docs & Assets"
              title="Back to Docs & Assets"
              size="sm"
              variant="ghost"
              onClick={onBrowseLibrary}
            >
              <ArrowLeft size={16} />
            </IconButton>
          ) : null}
          <Palette size={17} aria-hidden="true" />
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button variant="ghost" size="sm" minW={0} maxW="280px">
                <Text truncate>{activeCanvas?.title || 'Canvas'}</Text>
                <ChevronDown size={14} />
              </Button>
            </Menu.Trigger>
            <Portal container={panelRef}>
              <Menu.Positioner>
                <Menu.Content minW="260px">
                  {canvases.map(canvas =>
                    canvas.status === 'archived' ? (
                      <Menu.Item
                        key={canvas.id}
                        value={`restore-${canvas.id}`}
                        disabled={Boolean(restoringCanvasId)}
                        onClick={() => void restoreArchived(canvas.id)}
                      >
                        <RotateCcw size={14} />
                        <Text flex="1" truncate>
                          Restore {canvas.title}
                        </Text>
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        key={canvas.id}
                        value={canvas.id}
                        onClick={() => onSelectCanvas(toMetadata(canvas))}
                      >
                        <Text flex="1" truncate>
                          {canvas.title}
                        </Text>
                        {canvas.id === activeCanvas?.id ? <Check size={14} /> : null}
                      </Menu.Item>
                    )
                  )}
                  <Menu.Separator />
                  <Menu.Item value="create" onClick={() => void createBlank()}>
                    <Plus size={14} /> New canvas
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </HStack>
        <HStack gap="0.5" minW={0} overflowX="auto" css={{ scrollbarWidth: 'none' }}>
          {fixedFormat ? (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  aria-label="Canvas preview size"
                  title={`${fixedFormat.label} · ${fixedFormat.width}×${fixedFormat.height}`}
                  variant="ghost"
                  size="xs"
                >
                  {previewScaleMode === 'fit' ? 'Fit' : '100%'}
                  <ChevronDown size={13} aria-hidden="true" />
                </Button>
              </Menu.Trigger>
              <Portal container={panelRef}>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item value="fit" onClick={() => setPreviewScaleMode('fit')}>
                      Fit {previewScaleMode === 'fit' ? <Check size={14} /> : null}
                    </Menu.Item>
                    <Menu.Item value="actual" onClick={() => setPreviewScaleMode('actual')}>
                      Actual size (100%){' '}
                      {previewScaleMode === 'actual' ? <Check size={14} /> : null}
                    </Menu.Item>
                    <Menu.Separator />
                    <Menu.Item value="format" disabled>
                      {fixedFormat.label} · {fixedFormat.width}×{fixedFormat.height}
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          ) : null}
          <PreviewInspectorButton
            enabled={selectionMode}
            ready={Boolean((previewUrl || livePreviewUrl) && inspectorReady)}
            onToggle={() => {
              annotation.pause();
              setSelectionMode(current => !current);
            }}
          />
          <PreviewAnnotationToolbar
            annotation={annotation}
            disabled={!inspectorReady || !displayedVersion}
          />
          <IconButton
            aria-label="Refresh Canvas"
            title="Refresh Canvas"
            variant="ghost"
            size="sm"
            onClick={() => {
              annotation.reset();
              void refreshCanvas();
            }}
            loading={refreshing}
          >
            <RefreshCw size={16} />
          </IconButton>
          <IconButton
            aria-label="Share Canvas"
            title="Share Canvas"
            variant="ghost"
            size="sm"
            onClick={() => void share()}
            disabled={!activeCanvas}
          >
            <Share2 size={16} />
          </IconButton>
          <IconButton
            aria-label={fullscreen ? 'Exit fullscreen Canvas' : 'Open Canvas fullscreen'}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            variant="ghost"
            size="sm"
            onClick={() => void toggleFullscreen()}
            disabled={!activeCanvas}
          >
            {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </IconButton>
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton
                aria-label="Export Canvas"
                title={
                  exportReady
                    ? 'Export Canvas'
                    : 'Export available when the displayed version is published'
                }
                variant="ghost"
                size="sm"
                disabled={!exportReady}
              >
                <Download size={16} />
              </IconButton>
            </Menu.Trigger>
            <Portal container={panelRef}>
              <Menu.Positioner>
                <Menu.Content>
                  {['pdf', 'png', 'jpeg', 'webp', 'images', 'html'].map(format => (
                    <Menu.Item
                      key={format}
                      value={format}
                      disabled={!exportReady}
                      onClick={() => void download(format)}
                    >
                      {format === 'images'
                        ? 'Image ZIP'
                        : format === 'html'
                          ? 'Compiled HTML'
                          : format.toUpperCase()}
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
          <Box mx="1.5" h="5" w="1px" bg="ui.border.subtle" aria-hidden="true" />
          {activeCanvas ? (
            <Badge colorPalette={statusColor}>{activeCanvas.status.replace('_', ' ')}</Badge>
          ) : null}
          <Box mx="1" h="5" w="1px" bg="ui.border.subtle" aria-hidden="true" />
        </HStack>
        <SidePanelToggleButton open label="Close Canvas" onClick={() => onOpenChange(false)} />
      </HStack>

      <Flex
        ref={previewViewportRef}
        flex="1"
        minH={0}
        bg="app.bg"
        position="relative"
        align="stretch"
        overflow={fixedFormat ? 'auto' : 'hidden'}
      >
        {loading ? (
          <Flex flex="1" align="center" justify="center">
            <Spinner size="sm" />
          </Flex>
        ) : !activeCanvas ? (
          <VStack flex="1" justify="center" gap="4" px="10" textAlign="center">
            <Box p="4" borderRadius="2xl" bg="ui.surface.card">
              <Palette size={26} />
            </Box>
            <Box>
              <Text fontWeight="semibold">Turn research into something useful</Text>
              <Text color="fg.muted" textStyle="sm" mt="1">
                Create a deck, report, chart, one-pager, or interactive asset.
              </Text>
            </Box>
            <Button onClick={() => void createBlank()} loading={creating}>
              <Plus size={16} /> New canvas
            </Button>
          </VStack>
        ) : livePreviewUrl || previewUrl ? (
          <Box
            position="relative"
            flex={fixedFormat ? 'none' : '1'}
            w={fixedFormat ? `${fixedStageWidth}px` : '100%'}
            h={fixedFormat ? `${fixedStageHeight}px` : '100%'}
          >
            {previewUrl ? (
              <CanvasPreviewFrame
                src={previewUrl}
                live={false}
                title={activeCanvas.title}
                interactive={!displayLivePreview && !annotation.active}
                selectionMode={selectionMode && !displayLivePreview}
                onMessage={handleCompiledPreviewMessage}
                onSenderChange={handleCompiledPreviewSenderChange}
                onUnavailable={() => handleCompiledPreviewReadyChange(false)}
                onReadyChange={handleCompiledPreviewReadyChange}
                onFrameReset={() => {
                  if (!displayLivePreview) annotation.reset();
                }}
                frameStyle={{ ...canvasFrameStyle, zIndex: 0 }}
              />
            ) : null}
            {livePreviewUrl ? (
              <CanvasPreviewFrame
                src={livePreviewUrl}
                live
                title={`${activeCanvas.title} live preview`}
                interactive={displayLivePreview && !annotation.active}
                selectionMode={selectionMode && displayLivePreview}
                onMessage={handleLivePreviewMessage}
                onSenderChange={handleLivePreviewSenderChange}
                onUnavailable={() => handleLivePreviewReadyChange(false)}
                onReadyChange={handleLivePreviewReadyChange}
                onFrameReset={() => {
                  if (displayLivePreview) annotation.reset();
                }}
                frameStyle={{
                  ...canvasFrameStyle,
                  zIndex: 1,
                  opacity: displayLivePreview ? 1 : 0,
                  pointerEvents: displayLivePreview ? 'auto' : 'none',
                  transition: 'opacity 120ms ease-out',
                }}
              />
            ) : null}
            <PreviewAnnotationOverlay
              annotation={annotation}
              style={{ ...canvasFrameStyle, zIndex: 5 }}
            />
          </Box>
        ) : (
          <VStack flex="1" justify="center" gap="3">
            {activeCanvas.status === 'build_failed' || previewTimedOut ? null : (
              <Spinner size="sm" />
            )}
            <Text fontWeight="medium">
              {activeCanvas.status === 'build_failed'
                ? 'This version did not build'
                : previewTimedOut
                  ? 'Preview did not open'
                  : 'Bringing the canvas to life'}
            </Text>
            {activeCanvas.status === 'build_failed' || previewTimedOut ? (
              <Text textStyle="sm" color="fg.muted" maxW="360px" textAlign="center">
                {activeCanvas.status === 'build_failed'
                  ? 'The failed version is preserved. The last successful preview remains available from history.'
                  : 'The preview did not become available within 30 seconds.'}
              </Text>
            ) : null}
            {previewTimedOut ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPreviewTimedOut(false);
                  setPreviewRetry(current => current + 1);
                  void loadActiveCanvas().catch(() => undefined);
                  void loadPreview().catch(() => undefined);
                }}
              >
                <RefreshCw size={15} /> Retry preview
              </Button>
            ) : null}
          </VStack>
        )}
      </Flex>

      <Flex px="3" py="2.5" borderTopWidth="1px" align="center" gap="2">
        <History size={14} />
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button size="xs" variant="ghost">
              {displayedVersion ? `Version ${displayedVersion}` : 'No published version'}
              {displayLivePreview && activeCanvas?.status === 'building'
                ? ' · validating'
                : ''}{' '}
              <ChevronDown size={13} />
            </Button>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content>
              {readyVersions.map(version => (
                <Menu.Item
                  key={version.id}
                  value={String(version.version)}
                  onClick={() => selectVersion(version.version)}
                >
                  Version {version.version} ·{' '}
                  {formatCodexRelativeTime(version.completedAt || version.createdAt)}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </Flex>
    </Flex>
  );
}
